import React, { Suspense, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Switch, Route } from 'react-router-dom'
import jwt_decode from 'jwt-decode'
import axios from 'axios'
import { ToastContainer } from 'react-toastify'
import { handleErrors, clearAllErrors } from '../src/app/actions/errorsActions'
import GlobalModal from '../src/app/common/GlobalModal/GlobalModal'

import store from './store'
import setAuthToken from './app/utils/setAuthToken'
import { setCurrentUser, logoutUser, getRefreshToken } from './app/modules/Login/redux/authActions'
import { getMyTeam } from './app/modules/Users/redux/userActions'
import { RingCentralClient } from './app/lib/ringcentral/ringCentral'

import './App.scss'

// Importing Modules
import Loader from './app/modules/Loader/index'
import FileViewer from './app/common/PDFFileViewer/FileViewer'
import APIEmailComponents from './app/modules/EmailsMenu/APIComponents/index'
import PrivateRoute from './app/common/PrivateRoute'
import { BacktrackingProvider } from './app/context/urlBacktrackingContext'
import { LoginToRCRoute } from './app/modules/Users/User/LoginToRCRoute'
import { notificationDangerCustomTime } from './app/utils/customToastify'

const Login = React.lazy(() => import('./app/modules/Login/index'))
const ChangePassword = React.lazy(() => import('./app/modules/Login/ChangePassword'))
const SignInAs = React.lazy(() => import('./app/modules/Login/SignInAs'))
const Homepage = React.lazy(() => import('./app/layout/DefaultLayout'))
const SignInFromTS = React.lazy(() => import('./app/modules/Login/SignInFromTS'))

axios.defaults.baseURL = `${process.env.REACT_APP_BACKEND_URL}/api/v1`

// Auth rute se nalaze iza opcionog tajnog segmenta da ne bi bile lako otkrivene skenerima.
// Mora odgovarati AUTH_PATH_SECRET na backendu. Interceptor ubacuje segment u sve /auth/* pozive,
// pa pojedinačni pozivi i dalje koriste obične '/auth/...' putanje.
const AUTH_PATH_SECRET = (process.env.REACT_APP_AUTH_PATH_SECRET || '').trim().replace(/^\/+|\/+$/g, '')
if (AUTH_PATH_SECRET) {
  axios.interceptors.request.use((config) => {
    if (typeof config.url === 'string' && /^\/auth(\/|$)/.test(config.url)) {
      config.url = config.url.replace(/^\/auth/, `/auth/${AUTH_PATH_SECRET}`)
    }
    return config
  })
}

//Check for token
if (sessionStorage.CRMToken) {
  // Set headers
  setAuthToken(sessionStorage.CRMToken)
  // Get user info and exp
  const decoded = jwt_decode(sessionStorage.CRMToken)
  // Set user and isAuthenticated
  store.dispatch(setCurrentUser(decoded))
  // store.dispatch(getImage(decoded.userId, decoded.imageId, true))
  // NOTE: an expired access token is no longer a hard logout here — getRefreshToken() on mount
  // exchanges the DB session for a fresh token, and the 401 interceptor refreshes on demand.
  // Logout happens only if that refresh fails (dead/ended session).
}

let networkErrorShown = false
let forbiddenErrorShown = false
let sessionExpiredHandled = false

// Silent access-token refresh. The long-lived DB session (x-session-token, sent as a default
// header by setAuthToken) is exchanged for a fresh short-lived access token, so an expired JWT
// no longer forces a logout. A single in-flight refresh is shared across all concurrently-failing
// requests.
let isRefreshing = false
let refreshPromise = null

const refreshAccessToken = async () => {
  const resp = await axios.post('/auth/refresh-token')
  const newToken = resp?.data?.token
  if (!newToken) throw new Error('No token in refresh response')
  sessionStorage.setItem('CRMToken', newToken)
  setAuthToken(newToken)
  return newToken
}

const getServerErrorMessage = (err) => {
  const data = err?.response?.data
  if (!data) return null
  if (typeof data === 'string') return data
  if (typeof data?.message === 'string' && data.message.trim()) return data.message
  if (typeof data?.msg === 'string' && data.msg.trim()) return data.msg
  if (typeof data?.error === 'string' && data.error.trim()) return data.error
  return null
}

axios.interceptors.response.use(
  (response) => {
    store.dispatch(clearAllErrors())
    return response
  },
  async (err) => {
    if (err.code === 'ERR_NETWORK') {
      if (!networkErrorShown) {
        networkErrorShown = true
        notificationDangerCustomTime('Network Error', 5000, 'network-error')

        setTimeout(() => {
          networkErrorShown = false
        }, 3000)
      }

      return Promise.reject(err)
    }

    // Access token expired/invalid at runtime. Try one silent refresh via the DB session and
    // replay the original request; only if that fails do we log the user out. Auth endpoints are
    // excluded so a bad-credentials 401 (or a dead-session refresh 401) doesn't loop.
    const status = err?.response?.status
    const originalRequest = err?.config || {}
    const requestUrl = originalRequest.url || ''
    const isAuthEndpoint = /\/auth\/(?:[^/]+\/)?(session|login-as-user|loginAsUser|refresh-token|logout)\b/.test(
      requestUrl
    )

    if (status === 401 && !isAuthEndpoint && sessionStorage.getItem('CRMToken') && !originalRequest._retry) {
      const sessionToken = sessionStorage.getItem('sessionToken')
      if (sessionToken) {
        originalRequest._retry = true
        try {
          if (!isRefreshing) {
            isRefreshing = true
            refreshPromise = refreshAccessToken().finally(() => {
              isRefreshing = false
            })
          }
          const newToken = await refreshPromise
          originalRequest.headers = originalRequest.headers || {}
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return axios(originalRequest)
        } catch (refreshErr) {
          // fall through to logout
        }
      }
      // No session to refresh with, or refresh failed → clean logout + redirect to /login.
      if (!sessionExpiredHandled) {
        sessionExpiredHandled = true
        notificationDangerCustomTime('Your session has expired. Please sign in again.', 5000, 'session-expired')
        store.dispatch(logoutUser())
      }
      return Promise.reject(err)
    }

    if (err?.response?.status === 403) {
      const message = getServerErrorMessage(err)
      if (message && !forbiddenErrorShown) {
        forbiddenErrorShown = true
        notificationDangerCustomTime(message, 5000)
        setTimeout(() => {
          forbiddenErrorShown = false
        }, 3000)
      }
    }

    store.dispatch(handleErrors(err))
    return new Promise((resolve, reject) => {
      reject(err)
    })
  }
)

export default function App() {
  const { isAuth, userId } = useSelector((state) => ({
    isAuth: state.auth.isAuthenticated,
    userId: state.auth.user.userId,
  }))
  const dispatch = useDispatch()

  useEffect(() => {
    if (sessionStorage.CRMToken) {
      dispatch(getRefreshToken())
    }
  }, [])

  useEffect(() => {
    if (isAuth && userId) {
      dispatch(getMyTeam())
      new RingCentralClient(userId + '')
    }
  }, [isAuth, userId])

  return (
    <div style={{ userSelect: 'none' }}>
      <BacktrackingProvider>
        <Suspense fallback={<Loader />}>
          <ToastContainer />
          <GlobalModal />
          <Switch>
            <Route path='/login' component={Login} />
            <Route path='/change-password' component={ChangePassword} />
            <Route path='/file-viewer' component={FileViewer} />
            <Route path='/api-email' render={(props) => <APIEmailComponents {...props} />} />
            <Route path='/loginToRc' component={LoginToRCRoute} />
            <PrivateRoute path='/sign-in-as' component={SignInAs} />
            <PrivateRoute path='/sign-in-fromTS' component={SignInFromTS} />
            <PrivateRoute path='/' component={Homepage} />
          </Switch>
        </Suspense>
      </BacktrackingProvider>
    </div>
  )
}
